import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | failed
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "expired" || data.status === "failed") { setStatus("expired"); return; }
      } catch (e) {
        // Transient polling failure — log for observability, keep polling until max attempts
        console.warn("Payment status poll failed (will retry):", e?.message || e);
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= POLL_MAX_ATTEMPTS) { setStatus("expired"); return; }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="mx-auto max-w-2xl px-4 sm:px-6 py-20">
      <div className="border border-white/10 bg-zinc-950 p-8 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="h-10 w-10 text-blue-400 mx-auto mb-4 animate-spin"/>
            <h1 className="font-display text-2xl mb-2">Confirming your payment…</h1>
            <p className="text-white/60 text-sm">This usually takes a few seconds. Please don't close this tab.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4"/>
            <h1 className="font-display text-2xl mb-2">Payment confirmed</h1>
            <p className="text-white/60 text-sm mb-6">Your order has been placed successfully.</p>
            <Button data-testid="view-order-btn" onClick={() => nav(`/order/${orderId}`)} className="bg-blue-600 hover:bg-blue-500 rounded-none">
              View order details
            </Button>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="h-12 w-12 text-yellow-400 mx-auto mb-4"/>
            <h1 className="font-display text-2xl mb-2">Still waiting…</h1>
            <p className="text-white/60 text-sm mb-6">We couldn't confirm your payment in time. If money was deducted it will reflect within a few minutes. Contact us on 073968 71520 if there's an issue.</p>
            <Button onClick={() => nav(`/order/${orderId}`)} variant="outline" className="border-white/20 bg-transparent hover:bg-white/5 rounded-none">
              View order
            </Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4"/>
            <h1 className="font-display text-2xl mb-2">Invalid session</h1>
            <p className="text-white/60 text-sm">We couldn't locate your payment session.</p>
          </>
        )}
      </div>
    </div>
  );
}
